import { useState, useEffect } from 'react';
import api from '../services/api';
import LogoFull from './LogoFull.jpg';
import {
  LuEye, LuEyeOff, LuUserPlus,
} from 'react-icons/lu';
import GoogleSignInButton, { isGoogleConfigured } from './GoogleSignInButton';
import { authStyles as styles } from './Login';
import AuthLayout from './AuthLayout';

/**
 * Cadastro público: cria a empresa + o usuário administrador. Depois do
 * cadastro o usuário já entra logado e é levado para a escolha do plano
 * (App.js → PlanSelectionPage). Também permite criar a conta com o Google.
 */
const emptyForm = { full_name: '', email: '', phone: '', company_name: '', password: '', confirm: '' };

const maskPhone = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

export default function SignupPage({ onLogin, onBackToLogin }) {
  const [form, setForm] = useState(emptyForm);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [googleOn, setGoogleOn] = useState(false);

  useEffect(() => { isGoogleConfigured().then(setGoogleOn); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const finish = (data) => {
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    onLogin(data.user);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 8) { setError('A senha precisa ter pelo menos 8 caracteres.'); return; }
    if (form.password !== form.confirm) { setError('As senhas não conferem.'); return; }
    if (!acceptTerms) { setError('É preciso aceitar os Termos de Uso e a Política de Privacidade.'); return; }
    setIsLoading(true);
    try {
      const { data } = await api.post('/auth/signup', {
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        company_name: form.company_name,
        password: form.password,
        accept_terms: true,
      });
      finish(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Não foi possível criar a conta.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogle = async (credential) => {
    setError('');
    if (!acceptTerms) { setError('Marque o aceite dos Termos de Uso e da Política de Privacidade antes de continuar com o Google.'); return; }
    setIsLoading(true);
    try {
      const { data } = await api.post('/auth/google', { credential, accept_terms: true, company_name: form.company_name || undefined });
      if (data.requiresTwoFactor) {
        setError('Esta conta já existe e usa verificação em duas etapas. Entre pela tela de login.');
        return;
      }
      finish(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao continuar com o Google.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout compactPitch>
      <div style={{ ...styles.card, maxWidth: 460 }}>
        <div className="auth-card-logo">
          <img src={LogoFull} alt="DHR Consultoria" />
        </div>
        <div style={styles.divider} />

        <form onSubmit={handleSubmit} style={{ ...styles.form, gap: 14 }}>
          <div>
            <p style={{ ...styles.formTitle, fontSize: 18 }}><LuUserPlus style={{ marginRight: 8 }} /> Crie sua conta grátis</p>
            <p style={signupStyles.lead}>Sem cartão de crédito. Você escolhe o plano depois e pode mudar quando quiser.</p>
          </div>

          {error && <div style={styles.errorBox}>{error}</div>}

          <label style={signupStyles.terms}>
            <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} style={signupStyles.checkbox} />
            <span>Li e aceito os Termos de Uso e a Política de Privacidade da DHR Consultoria.</span>
          </label>

          {googleOn && (
            <>
              <GoogleSignInButton mode="signup" onCredential={handleGoogle} />
              <div style={styles.orDivider}><span style={styles.orLine} /><span>ou cadastre com e-mail</span><span style={styles.orLine} /></div>
            </>
          )}

          <div style={styles.field}>
            <label style={styles.label}>Nome completo *</label>
            <input style={styles.input} value={form.full_name} onChange={(e) => set('full_name', e.target.value)} autoComplete="name" required maxLength={200} />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>E-mail *</label>
            <input style={styles.input} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} autoComplete="email" required />
            <span style={signupStyles.hint}>Você vai usar este e-mail para entrar no sistema.</span>
          </div>
          <div style={signupStyles.row}>
            <div style={{ ...styles.field, flex: '1 1 170px' }}>
              <label style={styles.label}>Empresa *</label>
              <input style={styles.input} value={form.company_name} onChange={(e) => set('company_name', e.target.value)} autoComplete="organization" required maxLength={200} />
            </div>
            <div style={{ ...styles.field, flex: '1 1 170px' }}>
              <label style={styles.label}>Telefone</label>
              <input style={styles.input} type="tel" inputMode="numeric" value={form.phone} placeholder="(00) 00000-0000"
                onChange={(e) => set('phone', maskPhone(e.target.value))} autoComplete="tel" />
            </div>
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Senha *</label>
            <div style={styles.inputWrapper}>
              <input style={{ ...styles.input, paddingRight: 42 }} type={showPassword ? 'text' : 'password'} value={form.password}
                onChange={(e) => set('password', e.target.value)} autoComplete="new-password" required minLength={8} placeholder="Mínimo de 8 caracteres" />
              <button type="button" style={styles.eyeBtn} onClick={() => setShowPassword((v) => !v)} tabIndex={-1} aria-label="Mostrar senha">
                {showPassword ? <LuEyeOff /> : <LuEye />}
              </button>
            </div>
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Confirmar senha *</label>
            <input style={styles.input} type={showPassword ? 'text' : 'password'} value={form.confirm}
              onChange={(e) => set('confirm', e.target.value)} autoComplete="new-password" required />
          </div>

          <button style={{ ...styles.submitBtn, opacity: isLoading ? 0.75 : 1 }} type="submit" disabled={isLoading}>
            {isLoading ? 'Criando conta...' : 'Criar conta'}
          </button>

          <p style={styles.switchText}>
            Já tem conta?{' '}
            <button type="button" style={styles.switchLink} onClick={onBackToLogin}>Entrar</button>
          </p>
        </form>

        <div style={styles.footer}>
          © {new Date().getFullYear()} DHR Consultoria — Todos os direitos reservados
        </div>
      </div>
    </AuthLayout>
  );
}

const signupStyles = {
  row: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  lead: { margin: '4px 0 0', fontSize: 13, color: 'var(--text-2)', lineHeight: 1.45 },
  hint: { fontSize: 12, color: 'var(--text-3)' },
  terms: { display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: 'var(--text-2)', cursor: 'pointer', lineHeight: 1.4 },
  checkbox: { width: 16, height: 16, marginTop: 2, accentColor: 'var(--accent)', flexShrink: 0 },
};
